import React, { useState, useEffect } from 'react';
import Chart from 'react-apexcharts';
import { useTheme } from '@mui/material/styles';
import { fDate } from 'src/utils/format-time';
import { fPercent } from 'src/utils/format-number';
import { ApexOptions } from 'apexcharts';

type ChartType = 'line' | 'area' | 'bar' | 'pie' | 'donut' | 'radialBar' | 'scatter' | 'bubble' | 'heatmap' | 'candlestick' | 'boxPlot' | 'radar' | 'polarArea' | 'rangeBar' | 'rangeArea' | 'treemap';

interface ChartPoint {
    x: number;
    y: number;
}

interface ChartSeries {
    name: string;
    data: ChartPoint[];
}

interface CustomLineChartProps {
    series: ChartSeries[];
    color?: string;
    height?: number;
    width?: any;
    formatValue?: (value: number) => string;
    shouldCapValues?: boolean;
    type?: ChartType;
    granularity?: 'date_day' | 'year_week' | 'year_month';
    [key: string]: any;
}

const CustomLineChart: React.FC<CustomLineChartProps> = ({
    series,
    color = '#2D0857',
    height = 30,
    width = 100,
    type = 'line',
    formatValue = fPercent,
    shouldCapValues = false,
    granularity = 'date_day',
    ...props
}) => {
    const theme = useTheme();
    const [chartData, setChartData] = useState<ChartSeries[]>([]);

    useEffect(() => {
        if (series && series.length > 0) {
            // Nettoyer et valider les données
            const cleanedSeries = series.map(serie => ({
                ...serie,
                data: serie.data
                    ?.filter(point => {
                        // Vérifier que les points ont des valeurs valides
                        const validX = point.x != null && !isNaN(new Date(point.x).getTime());
                        const validY = point.y != null && !isNaN(point.y);
                        return validX && validY;
                    })
                    ?.map(point => ({
                        // Convertir les dates en timestamps si nécessaire
                        x: typeof point.x === 'string' ? new Date(point.x).getTime() : point.x,
                        y: Number(point.y)
                    }))
                    ?.sort((a, b) => a.x - b.x) || [] // Trier par date
            }));
            setChartData(cleanedSeries);
        }
    }, [series]);

    const options: ApexOptions = {
        chart: {
            sparkline: { enabled: true },
            animations: { enabled: false },
            toolbar: { show: false },
        },
        colors: [color],
        tooltip: {
            theme: theme.palette.mode === 'light' ? 'light' : 'dark',
            shared: true,
            followCursor: true,
            intersect: false,
            custom: ({ series, seriesIndex, dataPointIndex, w }: {
                series: number[][],
                seriesIndex: number,
                dataPointIndex: number,
                w: any
            }) => {
                const textColor = theme.palette.mode === 'light' ? '#212B36' : '#fff';
                const tooltipBgColor = theme.palette.mode === 'light' ? '#fff' : 'rgb(31, 38, 49)';
                const innerBgColor = theme.palette.mode === 'light' ? '#F4F6F8' : '#191f28';

                if (!series || !w.globals.seriesNames) return '';

                let formattedDate = '';
                try {
                    const dateStr = w.globals.seriesX[seriesIndex][dataPointIndex];
                    if (dateStr) {
                        const date = new Date(dateStr);
                        if (!isNaN(date.getTime())) {
                            formattedDate = fDate(date);
                        }
                    }
                } catch (error) {
                    console.warn('Error formatting date:', error);
                }

                const value = series[seriesIndex][dataPointIndex];
                const formattedValue = formatValue(value);

                return `
                    <div style="z-index: 9900!important; border-radius: 8px; color: ${textColor}; line-height: 3.2; text-align: center; background-color: ${tooltipBgColor}; box-shadow: 0 2px 6px rgba(0,0,0,0.15);">
                        <div style="text-align: center; line-height: 2; margin-bottom: 1px">
                            <span style="font-weight: 600; font-size: 12px; font-family: Public Sans;">${formattedDate}</span>
                        </div>
                        <div style="background-color: ${innerBgColor}; display: flex; align-items: center; margin-bottom: 1px; padding: 2px 8px; border-radius:8px;">
                            <span style="display: inline-block; width: 16px; height: 2px; margin-right: 8px; background-color: ${color}; margin-top: 1px;"></span>
                            <span style="font-size: 12px;">${w.globals.seriesNames[seriesIndex]}:</span><span style="font-size: 12px; font-weight: bold; margin-left: 4px;">${formattedValue}</span>
                        </div>
                    </div>
                `;
            },
            fixed: {
                enabled: false
            }
        },
        stroke: { width: 2, curve: 'smooth' as const },
        xaxis: {
            type: 'datetime' as const,
            labels: { show: false },
            axisBorder: { show: false },
            axisTicks: { show: false }
        },
        yaxis: { show: false },
        grid: { show: false,  },
        
    };

    return (
        <Chart
            type={type}
            series={chartData}
            options={options}
            height={height}
            width={width}
            {...props}
        />
    );
};

export default CustomLineChart;